// Venue-search VS1.5: what the venue page shows from the new venue facts
// (VS1.3, VS1.4). Plain functions, so tests can check every row. Field IDs,
// labels, lists and showIf rules come from the field dictionary (src/taxonomy);
// the other words from src/content/venuePage.js (Braeden, VS1.5 plan).
import copy from '../../../content/venuePage';
import { dictionary, fieldLabel, fromLegacy, toLegacy, valueLabel } from '../../../taxonomy';
import { isAnswered, isShown } from '../../../taxonomy/rules';
import { toMinutes } from '../../eventTimes';
import { choiceId, otherText, rowsOf } from '../HostView/inventory';
import { CAPACITY_FIELDS, FACT_FIELDS, FEATURE_FIELDS, m2ToSqft, PARKING_FIELDS, RESTROOM_FIELDS } from '../HostView/venueFacts';

// The terms with a "?" on the venue page (Braeden, VS1.5 plan, decision C).
export const PAGE_DEFINITIONS = [...CAPACITY_FIELDS, 'kitchen', 'space.rainBackup', 'space.danceArea', 'access.wheelchair', 'power.heavyDuty', 'rules.alcohol'];
const definitionOf = (id) => (PAGE_DEFINITIONS.includes(id) ? id : undefined);

// Old amenities with no new field (decision A): they join the Features list.
const KEPT_AMENITIES = ['runningWater', 'stagingRoom'];

const read = (venue, path) => path.split('.').reduce((value, key) => (value && typeof value === 'object' ? value[key] : undefined), venue);

// A flat map of field ID -> value, plus the setting as its ID (for showIf rules).
// Only answers of the right shape; anything else counts as not answered.
export const valuesOf = (venue) => {
  const values = { setting: fromLegacy('setting', venue?.setting) || venue?.setting };
  if (!venue) return values;
  FACT_FIELDS.forEach((id) => {
    const value = read(venue, id);
    if (isAnswered(id, value)) values[id] = value;
  });
  return values;
};

// A venue saved by the VS1.3 editor always has a kitchen answer (required).
export const hasNewFacts = (values) => values.kitchen !== undefined;

const number = (n) => n.toLocaleString('en-US');
const shown = (id, values) => values[id] !== undefined && isShown(id, values);

// '09:00' -> '9 AM'; '21:30' -> '9:30 PM'.
export const shortTime = (time) => {
  const minutes = toMinutes(time);
  if (minutes === null) return '';
  const hour = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hour % 12 || 12}${mins ? `:${String(mins).padStart(2, '0')}` : ''} ${hour < 12 ? 'AM' : 'PM'}`;
};

// The day price always with its hours (Braeden, VS1.3): "$1,500" + " · 9 AM–7 PM (10 hours)".
// An old venue with no times: "$1,500" + " / day", as before.
export const priceLine = (venue) => {
  const dollars = Number(String(venue?.dayPrice ?? '').replace(/[$,]/g, ''));
  const price = `$${number(Number.isFinite(dollars) ? dollars : 0)}`;
  const start = toMinutes(venue?.fullDay?.start);
  const end = toMinutes(venue?.fullDay?.end);
  if (start === null || end === null || end <= start) return { price, hours: copy.perDay };
  return { price, hours: ` · ${shortTime(venue.fullDay.start)}–${shortTime(venue.fullDay.end)} ${copy.hours((end - start) / 60)}` };
};

// The basics: a total first, the split after a tap (one pattern for every row).
export const basicsRows = (values) => {
  const rows = [];
  const b = copy.basics;

  const capacities = CAPACITY_FIELDS.filter((id) => shown(id, values));
  if (capacities.length) {
    const split = capacities.map((id) => ({ id, label: fieldLabel(id), value: number(values[id]), definition: definitionOf(id) }));
    [
      ['area.indoor', b.insideSpace],
      ['area.outdoor', b.outsideSpace]
    ].forEach(([id, label]) => {
      if (shown(id, values)) split.push({ id, label, value: b.sqft(m2ToSqft(values[id])) });
    });
    rows.push({ id: 'guests', label: b.guests, total: b.guestsTotal(Math.max(...capacities.map((id) => values[id]))), split });
  }

  if (PARKING_FIELDS.every((id) => values[id] !== undefined)) {
    const notes = [];
    if (values['parking.offSite'] > 0 && values['parking.shuttle'] !== undefined) notes.push(values['parking.shuttle'] ? b.shuttleYes : b.shuttleNo);
    rows.push({
      id: 'parking',
      label: b.parking,
      total: b.parkingTotal(PARKING_FIELDS.reduce((sum, id) => sum + values[id], 0)),
      split: PARKING_FIELDS.map((id) => ({ id, label: fieldLabel(id), value: number(values[id]) })),
      notes
    });
  }

  const restrooms = RESTROOM_FIELDS.filter((id) => values[id] !== undefined);
  if (restrooms.length) {
    rows.push({
      id: 'restrooms',
      label: b.restrooms,
      total: b.restroomsTotal(restrooms.reduce((sum, id) => sum + values[id], 0)),
      split: restrooms.map((id) => ({ id, label: b.restroomLabels[id], value: number(values[id]) }))
    });
  }

  if (values.kitchen !== undefined) {
    rows.push({ id: 'kitchen', label: b.kitchen, total: valueLabel('kitchen', choiceId(values.kitchen)) || otherText(values.kitchen), definition: 'kitchen' });
  }

  if (values['overnight.allowed'] !== undefined) {
    const yes = values['overnight.allowed'] === true;
    const split = [];
    if (yes && shown('overnight.beds', values)) {
      const beds = Object.entries(values['overnight.beds'])
        .filter(([, count]) => count > 0)
        .map(([id, count]) => `${valueLabel('bedType', id) || id} ${number(count)}`);
      if (beds.length) split.push({ id: 'overnight.beds', label: b.beds, value: beds.join(', ') });
    }
    if (yes && shown('overnight.showers', values)) split.push({ id: 'overnight.showers', label: b.showers, value: number(values['overnight.showers']) });
    rows.push({ id: 'overnight', label: b.overnight, total: yes ? b.yes : b.no, split, notes: yes ? [b.overnightNote] : [] });
  }

  return rows;
};

// Features: only the "yes" answers (decision B), then the 2 old amenities with no new field.
export const featureRows = (values, amenities) => {
  const rows = FEATURE_FIELDS.filter((id) => values[id] === true).map((id) => {
    const row = { id, label: fieldLabel(id), definition: definitionOf(id) };
    if (id === 'lighting.outdoor' && shown('lighting.details', values)) {
      row.details = rowsOf(values['lighting.details'])
        .map((light) => {
          const kind = valueLabel('lightKind', choiceId(light.kind)) || otherText(light.kind);
          const places = (Array.isArray(light.places) ? light.places : []).map((place) => valueLabel('lightPlace', place)).filter(Boolean);
          return kind && (places.length ? `${kind}: ${places.join(', ')}` : kind);
        })
        .filter(Boolean);
    }
    return row;
  });
  const stored = Array.isArray(amenities) ? amenities : [];
  KEPT_AMENITIES.filter((id) => stored.includes(toLegacy('amenity', id))).forEach((id) => rows.push({ id: `amenity.${id}`, label: valueLabel('amenity', id) }));
  return rows;
};

// A choice's words: the list label, or the host's own "Other" text.
const choiceLabel = (list, choice) => {
  if (choice === undefined || choice === null) return '';
  return choiceId(choice) === 'other' ? otherText(choice).trim() : valueLabel(list, choiceId(choice)) || '';
};

// The items: "Tables · 12 · Round · 5 ft (60 in)", the note under it. Rentals: names only (D-0074).
export const inventoryView = (values) => {
  const rows = rowsOf(values.inventory);
  const parts = (row) => dictionary.lists.inventoryItem.parts[choiceId(row.item)] || {};
  const name = (row) => choiceLabel('inventoryItem', row.item);
  const included = rows
    .filter((row) => row.pricing === 'included' && row.count > 0 && name(row))
    .map((row) => {
      const { kind, size } = parts(row);
      const details = [number(row.count), kind && choiceLabel(kind, row.kind), size && choiceLabel(size, row.size)].filter(Boolean);
      return { name: name(row), details, note: typeof row.note === 'string' ? row.note.trim() : '' };
    });
  const forRent = [
    ...new Set(
      rows
        .filter((row) => row.pricing === 'costsExtra')
        .map(name)
        .filter(Boolean)
    )
  ];
  return { included, forRent };
};

// "Ask about rentals →": an email to support with the venue name filled in (D-0074).
export const rentalsMailto = (email, venueName) =>
  `mailto:${email}?subject=${encodeURIComponent(copy.rent.subject(venueName))}&body=${encodeURIComponent(copy.rent.body(venueName))}`;

// Rules: both answers always show (decision B); not answered: hidden.
export const ruleRows = (values) => {
  const r = copy.rules;
  const rows = Object.keys(r.labels)
    .filter((id) => values[id] !== undefined)
    .map((id) => {
      let value = values[id] ? r.allowed : r.notAllowed;
      if (id === 'rules.amplifiedMusic' && values[id] && shown('rules.musicEndTime', values)) value = r.allowedUntil(shortTime(values['rules.musicEndTime']));
      return { id, label: r.labels[id], value, definition: definitionOf(id) };
    });
  const notAllowed = values['rules.eventTypesNotAllowed'];
  const ids = Array.isArray(notAllowed) ? notAllowed : rowsOf(notAllowed?.list);
  const names = ids.filter((id) => id !== 'other').map((id) => valueLabel('eventType', id) || id);
  if (ids.includes('other') && otherText(notAllowed).trim()) names.push(otherText(notAllowed).trim());
  if (names.length) rows.push({ id: 'rules.eventTypesNotAllowed', label: r.eventTypesNotAllowed, value: names.join(', ') });
  return rows;
};
